import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Stack,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  SCENARIO_CATEGORIES,
  TIERS,
  REGIONS,
} from '../../data/pricingScenarios';

const MENU_PROPS = {
  PaperProps: { style: { maxHeight: 300 } },
};

const STATUS_OPTIONS = [
  { value: 'passed', labelKey: 'testScenarios.passedOnly', fallback: 'Passed' },
  { value: 'failed', labelKey: 'testScenarios.failedOnly', fallback: 'Failed' },
];

export default function TestFilters({
  filters,
  onFilterChange,
  stats,
}) {
  const { t } = useTranslation();

  const handleChange = (field) => (event) => {
    onFilterChange({
      ...filters,
      [field]: event.target.value,
    });
  };

  const renderChips = (selected, labelMap) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => (
        <Chip key={value} label={labelMap(value)} size="small" />
      ))}
    </Box>
  );

  return (
    <Box sx={{ mb: 3 }}>
      {/* Statistics chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip
          label={t('testScenarios.total', 'Total: {{count}}', { count: stats.total })}
          color="default"
          size="small"
        />
        <Chip
          label={t('testScenarios.passed', 'Passed: {{count}}', { count: stats.passed })}
          color="success"
          size="small"
        />
        <Chip
          label={t('testScenarios.failed', 'Failed: {{count}}', { count: stats.failed })}
          color="error"
          size="small"
          sx={{ display: stats.failed > 0 ? 'flex' : 'none' }}
        />
      </Stack>

      {/* Filter controls */}
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {/* Search */}
        <TextField
          size="small"
          label={t('testScenarios.search', 'Search')}
          placeholder={t('testScenarios.searchPlaceholder', 'Search by description...')}
          value={filters.search}
          onChange={handleChange('search')}
          sx={{ minWidth: 200 }}
        />

        {/* Category filter (multi-select) */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('testScenarios.product', 'Product')}</InputLabel>
          <Select
            multiple
            value={filters.category}
            label={t('testScenarios.product', 'Product')}
            onChange={handleChange('category')}
            renderValue={(selected) => renderChips(selected, (v) => SCENARIO_CATEGORIES[v] || v)}
            MenuProps={MENU_PROPS}
          >
            {Object.entries(SCENARIO_CATEGORIES).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                <Checkbox checked={filters.category.includes(key)} size="small" />
                <ListItemText primary={label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Tier filter (multi-select) */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('testScenarios.tier', 'Tier')}</InputLabel>
          <Select
            multiple
            value={filters.tier}
            label={t('testScenarios.tier', 'Tier')}
            onChange={handleChange('tier')}
            renderValue={(selected) => renderChips(selected, (v) => v.charAt(0).toUpperCase() + v.slice(1))}
            MenuProps={MENU_PROPS}
          >
            {TIERS.map((tier) => (
              <MenuItem key={tier} value={tier}>
                <Checkbox checked={filters.tier.includes(tier)} size="small" />
                <ListItemText primary={tier.charAt(0).toUpperCase() + tier.slice(1)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Region filter (multi-select) */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('testScenarios.region', 'Region')}</InputLabel>
          <Select
            multiple
            value={filters.region}
            label={t('testScenarios.region', 'Region')}
            onChange={handleChange('region')}
            renderValue={(selected) => renderChips(selected, (v) => v.toUpperCase())}
            MenuProps={MENU_PROPS}
          >
            {REGIONS.map((region) => (
              <MenuItem key={region} value={region}>
                <Checkbox checked={filters.region.includes(region)} size="small" />
                <ListItemText primary={region.toUpperCase()} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status filter (multi-select) */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('testScenarios.status', 'Status')}</InputLabel>
          <Select
            multiple
            value={filters.status}
            label={t('testScenarios.status', 'Status')}
            onChange={handleChange('status')}
            renderValue={(selected) => renderChips(selected, (v) => {
              const opt = STATUS_OPTIONS.find(o => o.value === v);
              return opt ? t(opt.labelKey, opt.fallback) : v;
            })}
            MenuProps={MENU_PROPS}
          >
            {STATUS_OPTIONS.map(({ value, labelKey, fallback }) => (
              <MenuItem key={value} value={value}>
                <Checkbox checked={filters.status.includes(value)} size="small" />
                <ListItemText primary={t(labelKey, fallback)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );
}
